// 2) Third-party libraries
import { fetchAuthSession } from "@aws-amplify/auth";
import { get, map, filter as lFilter } from "lodash-es";
import { all, call, fork, put, takeLatest, delay, select } from "redux-saga/effects";

// 3) Local imports
// 3.1 Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from "../../config/config";
import { CUSTOMER_ASSISTANCE_PAGE, DETAILS_TAB } from "../../config/tableAndPageConstants";
import { FamilyNameAttribute, GivenNameAttribute } from "../../constants/authUserAttributes";
import { communicationResourceReplyType } from "../../constants/common";


// 3.3 Project-specific modules � components/types/other
import dayjs from "src/config/dayjs";
import { appliedFilterIndicator } from "../../constants/filterbar";
import { _queries as queries } from "../../graphql/queries.graphql";
import { PageData } from "../common/types";
import { Ticket } from "./types";
import client, { post } from "src/api";
import { getOrganisationActionUrl } from "../organisations/organisationSlice";

// 3.4 Utility functions / types
import {
    checkIfEmailIsValid,
    checkShouldRequestRefetch,
    customPostData,
    formatDateToDateObjectUTC,
    getRegionallyGraphqlResponse,
    removeAppliedFiltersForApiRequest,
    populateSortFieldsParamSortParams as populateTaskSortFieldsParamSortParams,
    getTicketDiscussionPayload,
} from "../../utils/commonFunctions";
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import { getCurrentUser } from "../auth/authSlice";
import { getCloudImportType } from '../companies/companySlice';
import {
    getTicketDataErrorAction,
    getTicketDataInvoicesErrorAction,
    getTicketDataInvoicesSuccessAction,
    getTicketDataSuccessAction,
    getTicketDataTasksErrorAction,
    getTicketDataTasksSuccessAction,
    getTicketsErrorAction,
    getTicketsSuccessAction,
    setSelectedTicketIdSuccessAction,
    getTicketsRequestAction,
    getTicketConversationSuccessAction,
    getTicketConversationErrorAction,
    getTicketDiscussionSuccessAction,
    getTicketDiscussionErrorAction,
    getActionResolveTicketOptionsRequestAction,
    ActionTicketRequestAction,
    checkTicketsReadyStateAction,
    getTicketCustomFieldsForCompanyRequestAction,
    getTicketCustomFieldsForOrganisationRequestAction,
    ticketAssignUserRequestAction,
    ticketResourceReplyRequestAction,
    ticketAddCommentRequestAction,
    getTicketDiscussionRequestAction,
    getTicketConversationRequestAction,
    resolveOrganisationQueryRequestAction,
    resolveQueryRequestAction,
    getResolveTicketOptionsRequestAction,
    getTicketDataTasksRequestAction,
    getTicketDataInvoicesRequestAction,
    setSelectedTicketIdRequestAction,
    getTicketInfoForChatValidation,
    getTicketDataRequestAction,
    getTicketsPageData,
} from "./ticketSlice";

const preparePayloadForTaskFilter = ({ payload, canExcludeTasks, parseCustomFields }: {
    payload: any,
    canExcludeTasks: boolean,
    parseCustomFields: boolean
}) => {
    const {
        filter,
        filters,
        sortBy,
        sortAscending,
        usingMultipleWorkflow,
        isPaymentPlanEnabled,
        taskIds,
        excludeTasks,
        recordLimit,
        ticketIds,
        excludeTickets
    } = payload;

    const finalPayload: any = {
        Ascending: sortAscending,
        ...(
            canExcludeTasks ? {
                TaskIds: taskIds,
                ExcludeTasks: excludeTasks,
                RecordLimit: recordLimit,
                TicketIds: ticketIds,
                ExcludeTickets: excludeTickets
            } : {}
        )
    };
    const finalFilter = filter || filters; // Handle different filter property names in models

    if (finalFilter) {
        const typeValue = isPaymentPlanEnabled
            ? finalFilter.Type
            : lFilter(finalFilter.Type, (ft: any) => ft !== 8);
        const usedFilters = {
            ...finalFilter,
            Type: typeValue,
            [`Type${appliedFilterIndicator}`]: typeValue,
        };
        const cleanFilters = removeAppliedFiltersForApiRequest(
            usedFilters,
            true,
            'task'
        );
        const assignedUserValue = cleanFilters.AssignedUserId;
        if (assignedUserValue) {
            if (checkIfEmailIsValid(assignedUserValue)) {
                delete cleanFilters.AssignedUserId;
                cleanFilters.AssignedEmailAddress = assignedUserValue;
            }
        }
        if (parseCustomFields) {
            if (cleanFilters.CustomFieldFilters) {
                cleanFilters.CustomFieldFilters = JSON.parse(
                    cleanFilters.CustomFieldFilters
                );
            }
        }
        if (!usingMultipleWorkflow) delete cleanFilters.WorkflowIds;
        Object.assign(finalPayload, cleanFilters);
    }

    if (sortBy) {
        const sortFields = populateTaskSortFieldsParamSortParams(sortBy);
        if (parseCustomFields) {
            if (sortFields.CustomFieldSort) {
                sortFields.CustomFieldSort = JSON.parse(
                    sortFields.CustomFieldSort
                );
            }
        }
        Object.assign(finalPayload, sortFields);
    }

    return finalPayload;
}

/**
 * Function called to prepare the sort values to be passed to the API.
 * @param sortBy
 */
export const populateSortFieldsParamSortParams = (sortBy: string) => {
    const sortFields = { SortField: sortBy, ThenSortField: '' };

    return sortFields;
};

let refetchCount = 0;
/**
 * Function that fetches the tickets from API.
 * @param param0
 */
function* handleGetTicketsRequest({ payload }: any) {
    const errorMessage = 'Error fetching ticket list. Please try again later.';
    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
        } = payload;

        // To call async functions, use redux-saga's `call()`.
        const sortFields = populateSortFieldsParamSortParams(sortBy);

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'ticket',
            true
        );

        const assignedUserValue = cleanFilters.AssignedUserId;

        if (assignedUserValue) {
            if (checkIfEmailIsValid(assignedUserValue)) {
                delete cleanFilters.AssignedUserId;
                cleanFilters.AssignedEmailAddress = assignedUserValue;
            }
        }

        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TICKETS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    ...sortFields,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * CUSTOMER_ASSISTANCE_PAGE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            }
        );

        const { Tickets } = get(res.data, 'GetTicketsForCompany');

        if (Tickets) {
            const responsePayload = {
                data: Tickets,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Tickets.length < pageSize) &&
                        !(pageSize < CUSTOMER_ASSISTANCE_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getTicketsSuccessAction(responsePayload));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTicketsRequestAction(payload));
        }
        else {
            yield put(getTicketsErrorAction([errorMessage]));
        }
    }
}

/**
 * Function that fetches the ticket details based on the given ticketId.
 */
function* handleGetTicketDataRequest({
    payload: { ticketId, isUsingCloudImportType },
}: any) {
    const errorMessage =
        'Error fetching ticket details. Please try again later.';

    try {
        const cloudImportType: string = yield select(getCloudImportType);
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TICKET_DETAILS_FOR_COMPANY,
                variables: {
                    TicketId: ticketId,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            }
        );

        const Ticket = get(res.data, 'GetTicketDetailsForCompany');

        if (Ticket) {
            const responsePayload = {
                record: Ticket,
            };

            yield put(getTicketDataSuccessAction(responsePayload));
        }
        else {
            yield put(getTicketDataErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTicketDataErrorAction([errorMessage]));
    }
}

/**
 * Function that fetches the ticket details for chat validation.
 */
function* handleGetTicketInfoForChatValidationRequest({
    payload: {
        callback,
        ...requestPayload
    }
}: any) {
    const errorMessage =
        'Error fetching ticket details. Please try again later.';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TICKET_INFO_FOR_CHAT_VALIDATION,
                variables: requestPayload
            }
        );

        const Ticket = get(res.data, 'GetTicketInfoForChatValidation');

        if (Ticket) {
            const responsePayload = {
                Record: Ticket,
                IsSuccess: true,
                Messages: []
            };

            callback(responsePayload);
        }
        else {
            throw Error(errorMessage);
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        callback({
            IsSuccess: false,
            Messages: [errorMessage]
        });
    }
}

/**
 * Function that sets the selectedId upon clicking the row item.
 * @param param0
 */
function* handleSetSelectedTicketIdRequest({ payload }: any) {
    const { ticketId, callback } = payload;
    yield put(setSelectedTicketIdSuccessAction(ticketId));

    callback();
}

/**
 * Ticket Data Invoices
 */
/**
 * Function that fetches the invoices for the selected ticket.
 * @param param0
 */
function* handleGetTicketDataInvoicesRequest({ payload }: any) {
    const errorMessage = `Error fetching ticket's invoice list. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            invoiceState,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.INVOICE_LIST.pageSize,
                }
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForCompany');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Invoices.length < pageSize) &&
                        !(pageSize < DETAILS_TAB.INVOICE_LIST.pageSize),
                },
            };

            yield put(getTicketDataInvoicesSuccessAction(responsePayload));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTicketDataInvoicesErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the tasks data for the selected ticket.
 * @param param0
 */
function* handleGetTicketDataTasksRequest({ payload }: any) {
    const errorMessage = `Error fetching ticket's tasks list. Please try again later.`;

    try {
        const {
            filters,
            // sortBy,
            // sortAscending,
            pageSize,
            currentPage,
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASKS_FOR_RESOURCE,
                variables: {
                    ...cleanFilters,
                    // SortField: sortBy,
                    // Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * CUSTOMER_ASSISTANCE_PAGE.dataTasks.pageSize,
                }
            }
        );

        const { Tasks } = get(res.data, 'GetTasksForResource');

        if (Tasks) {
            const responsePayload = {
                data: Tasks,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Tasks.length < pageSize) &&
                        !(
                            pageSize <
                            CUSTOMER_ASSISTANCE_PAGE.dataTasks.pageSize
                        ),
                },
            };

            yield put(getTicketDataTasksSuccessAction(responsePayload));
        }
        else {
            yield put(getTicketDataTasksErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTicketDataTasksErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the `Action` options on resolving ticket panel.
 * @param param0
 */
function* handleGetResolveTicketOptionsRequest({ payload: sagaPayload }: any) {
    const { workflowId, stateName, companyId, callback } = sagaPayload;
    try {
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_TRIGGERS_FOR_WORKFLOW_STATE, 
                variables: {
                    CompanyId: companyId,
                    WorkflowId: workflowId,
                    StateName: stateName
                }
            }
        );

        const { WorkflowTransitions: triggerOptions } = get(
            res,
            'data.GetTriggersForWorkflowState'
        );

        if (callback) {
            callback(triggerOptions);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when resolving a ticket.
 * @param param0
 */
function* handleResolveQueryRequest({ payload: sagaPayload }: any) {
    const { callback, filter, ...fields } = sagaPayload;
    const {
        CompanyId,
    } = filter;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: filter, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId },
        ...fields,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/ticket',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Ticket has been resolved successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleOrganisationResolveQueryRequest({ payload: sagaPayload }: any) {
    const { callback, filter, ...fields } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: filter, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
        ...fields,
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};
        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');
            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/ticket`,
                payload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/ticket',
                options: {
                    body: payload,
                }
            });
        }
        
        const { TaskEditAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Ticket has been resolved successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for fetching the discussion list of a given ticket.
 * @param param0
 */
function* handleGetTicketDiscussionRequest({ payload }: any) {
    const errorMessage = `Error fetching ticket's discussion list. Please try again later.`;

    try {
        const {
            finalPayload,
            currentPage,
            pageSize
        } = getTicketDiscussionPayload(payload);

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CHAT_FOR_COMPANY,
            finalPayload
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationResourcesForCompany'
        );

        if (ConversationLines) {
            const responsePayload = {
                data: ConversationLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage: ConversationLines.length >= pageSize
                }
            };

            yield put(getTicketDiscussionSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTicketDiscussionErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given ticket.
 * @param param0
 */
function* handleGetTicketConversationRequest({ payload }: any) {
    const errorMessage = `Error fetching ticket's conversation list. Please try again later.`;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } =
            payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            undefined,
            true
        );

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONVERSATION_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                }
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForCompany'
        );
        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getTicketConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTicketConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for adding comment to the ticket's conversation list.
 * @param param0
 */
function* handleTicketAddCommentRequest({ payload: sagaPayload }: any) {
    const { filter, ticketIds, excludeTickets, comment, callback } = sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'ticket'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        TicketManagementFilter: {
            ...cleanFilters,
            TicketIds: ticketIds,
            ExcludeTickets: excludeTickets,
        },
        Comment: comment,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/conversation/save/ticketcomment',
            options: {
                body: payload
            }
        });

        if (callback) {
            let RefetchList = true;

            if (ticketIds.length === 1 && excludeTickets === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const ticketsUpdated: DynamicObject[] = map(
                    ticketIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getTicketsPageData);

                yield put(
                    getTicketsSuccessAction({
                        data: ticketsUpdated as Ticket[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for requesting resource reply.
 * @param param0
 */
function* handleTicketResourceReplyRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;
    const payload = {
        ...fields,
        ResourceType: communicationResourceReplyType.Ticket
    };

    try {
        // To call async functions, use redux-saga's `call()`.
        yield call(post, {
            apiName: API_NAME,
            path: '/communication/resource/reply',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

// assign user
/**
 * Function called for assign user to tickets.
 * @param param0
 */
function* handleTicketAssignUserRequest({
    payload: sagaPayload,
}: any) {
    const { filter, ticketIds, excludeTickets, userObject, callback, companyId } =
        sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'ticket'
    );

    const payload = {
        TicketManagementFilter: {
            ...cleanFilters,
            TicketIds: ticketIds,
            ExcludeTickets: excludeTickets,
            CompanyId: companyId
        },
        ...userObject,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/ticket/action/assign-user',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const successMessages: any = [];
        successMessages.push(
            `Ticket${payload.TicketManagementFilter.ExcludeTickets || payload.TicketManagementFilter.TicketIds.length > 1 ? 's have' : ' has'
            } been assigned successfully!`
        );

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the ticket custom fields for a company.
 * @param param0
 */
function* handleGetTicketCustomFieldsForCompanyRequest({ payload }: any) {
    //const errorMessage = '';
    const {
        CompanyId,
        FieldName,
        callback
    } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TICKET_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues = get(res.data, 'GetTicketCustomFieldsForCompany');

        if (callback) {
            CustomFieldValues.IsSuccess = true;

            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

/**
 * Function for getting the ticket custom fields for a company.
 * @param param0
 */
function* handleGetTicketCustomFieldsForOrganisationRequest({ payload }: any) {
    //const errorMessage = '';
    const {
        CompanyIds,
        FieldName,
        callback
    } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TICKET_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues = get(res.data, 'GetTicketCustomFieldsForOrganisation');

        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for checking the ready state of tasks selected to enable/disable the `Action` button in
 * Active tasks page.
 * @param param0
 */
function* handleCheckTicketsReadyStateRequest({ payload }: any) {
    const {
        pageSize,
        currentPage,
        callback,
    } = payload;

    // To call async functions, use redux-saga's `call()`.
    const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: false });

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.CHECK_TICKETS_ACTION_STATE, 
                variables: {
                    ...commonTaskFilter,
                    PageSize: pageSize,
                    Skip: currentPage * pageSize,
                }
            }
        );

        const tasksActionState = get(res.data, 'TicketsActionState');

        if (callback) callback(tasksActionState);
    } catch (err) {
        if (callback) {
            callback(false);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when actioning a ticket.
 * @param param0
 */
function* handleActionRequest({ payload: sagaPayload }: any) {
    const { callback, filter, ticketIds, excludeTickets, companyId, ...fields } = sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'ticket'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        TicketManagementFilter: {
            ...cleanFilters,
            TicketIds: ticketIds,
            ExcludeTickets: excludeTickets,
            CompanyId: companyId,
        },
        ...fields,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/ticket/action/ticket',
            options: {
                body: payload,
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        let successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Ticket has been resolved successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;
        
            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the tasks data for the selected ticket.
 * @param param0
 */
function* handleActionResolveTicketOptionsRequest({ payload: sagaPayload }: any) {
    const errorMessage = `Error fetching ticket's tasks list. Please try again later.`;

    try {
        const {
            filters,
            pageSize,
            currentPage,
            companyId,
            callback
        } = sagaPayload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASKS_FOR_RESOURCE, 
                variables: {
                    ...cleanFilters,
                    PageSize: pageSize,
                    Skip: currentPage * CUSTOMER_ASSISTANCE_PAGE.dataTasks.pageSize,
                }
            }
        );

        const { Tasks } = get(res.data, 'GetTasksForResource');
        
        if (Tasks.length > 0) {
            const workflowId = Tasks[0].Workflow.WorkflowId;
            const stateName = Tasks[0].Workflow.StateName;

            try {
                const res: DynamicObject = yield call(
                    [client, client.graphql],
                    {
                        query: queries.GET_TRIGGERS_FOR_WORKFLOW_STATE,
                        variables: {
                            CompanyId: companyId,
                            WorkflowId: workflowId,
                            StateName: stateName
                        }
                    }
                );

                const { WorkflowTransitions: triggerOptions } = get(
                    res,
                    'data.GetTriggersForWorkflowState'
                );

                if (callback) {
                    callback(triggerOptions);
                }
            } catch (err) {
                if (callback) {
                    callback([]);
                }

                if (err instanceof Error) {
                    console.log('Error', err);
                } else {
                    console.error('An unknown error occured.');
                }
            }
        } else if (callback) {
            callback();
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTicketDataTasksErrorAction([errorMessage]));
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetTicketsRequest() {
    yield takeLatest(
        getTicketsRequestAction.type,
        handleGetTicketsRequest
    );
}

function* watchGetTicketDataRequest() {
    yield takeLatest(
        getTicketDataRequestAction.type,
        handleGetTicketDataRequest
    );
}

function* watchGetTicketInfoForChatValidationRequest() {
    yield takeLatest(
        getTicketInfoForChatValidation.type,
        handleGetTicketInfoForChatValidationRequest
    );
}

function* watchSetSelectedTicketIdRequest() {
    yield takeLatest(
        setSelectedTicketIdRequestAction.type,
        handleSetSelectedTicketIdRequest
    );
}

// Task Data Invoices
function* watchGetTicketDataInvoicesRequest() {
    yield takeLatest(
        getTicketDataInvoicesRequestAction.type,
        handleGetTicketDataInvoicesRequest
    );
}

// Notification Tickets
function* watchGetTicketDataTicketsRequest() {
    yield takeLatest(
        getTicketDataTasksRequestAction.type,
        handleGetTicketDataTasksRequest
    );
}

function* watchGetResolveTicketOptionsRequest() {
    yield takeLatest(
        getResolveTicketOptionsRequestAction.type,
        handleGetResolveTicketOptionsRequest
    );
}

function* watchResolveQueryRequest() {
    yield takeLatest(
        resolveQueryRequestAction.type,
        handleResolveQueryRequest
    );
}

function* watchOrganisationResolveQueryRequest() {
    yield takeLatest(
        resolveOrganisationQueryRequestAction.type,
        handleOrganisationResolveQueryRequest
    );
}

function* watchGetTicketConversationRequest() {
    yield takeLatest(
        getTicketConversationRequestAction.type,
        handleGetTicketConversationRequest
    );
}

function* watchGetTicketDiscussionRequest() {
    yield takeLatest(
        getTicketDiscussionRequestAction.type,
        handleGetTicketDiscussionRequest
    );
}

function* watchTicketAddCommentRequest() {
    yield takeLatest(
        ticketAddCommentRequestAction.type,
        handleTicketAddCommentRequest
    );
}

function* watchTicketResourceReplyRequest() {
    yield takeLatest(
        ticketResourceReplyRequestAction.type,
        handleTicketResourceReplyRequest
    );
}

function* watchTicketAssignUserRequest() {
    yield takeLatest(
        ticketAssignUserRequestAction.type,
        handleTicketAssignUserRequest
    );
}

function* watchGetTicketCustomFieldsForCompanyRequest() {
    yield takeLatest(
        getTicketCustomFieldsForCompanyRequestAction.type,
        handleGetTicketCustomFieldsForCompanyRequest
    );
}

function* watchGetTicketCustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getTicketCustomFieldsForOrganisationRequestAction.type,
        handleGetTicketCustomFieldsForOrganisationRequest
    );
}

function* watchCheckTicketsReadyStateRequest() {
    yield takeLatest(
        checkTicketsReadyStateAction.type,
        handleCheckTicketsReadyStateRequest
    );
}

function* watchActionRequest() {
    yield takeLatest(
        ActionTicketRequestAction.type,
        handleActionRequest
    );
}

function* watchActionResolveTicketOptionsRequest() {
    yield takeLatest(
        getActionResolveTicketOptionsRequestAction.type,
        handleActionResolveTicketOptionsRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* ticketsSaga() {
    yield all([
        fork(watchGetTicketsRequest),
        fork(watchGetTicketDataRequest),
        fork(watchGetTicketInfoForChatValidationRequest),
        fork(watchSetSelectedTicketIdRequest),
        fork(watchGetTicketDataInvoicesRequest),
        fork(watchGetTicketDataTicketsRequest),
        fork(watchGetResolveTicketOptionsRequest),
        fork(watchResolveQueryRequest),
        fork(watchOrganisationResolveQueryRequest),
        fork(watchGetTicketConversationRequest),
        fork(watchGetTicketDiscussionRequest),
        fork(watchTicketAddCommentRequest),
        fork(watchTicketResourceReplyRequest),
        fork(watchTicketAssignUserRequest),
        fork(watchGetTicketCustomFieldsForCompanyRequest),
        fork(watchGetTicketCustomFieldsForOrganisationRequest),
        fork(watchCheckTicketsReadyStateRequest),
        fork(watchActionRequest),
        fork(watchActionResolveTicketOptionsRequest)
    ]);
}

export default ticketsSaga;